import { flow } from 'fp-ts/function';
import { get, identity, isNil, pickBy } from 'lodash/fp';
import { createSelector } from 'reselect';

import { IMAGE_DEFAULT_QUALITY, IMAGE_DEFAULT_SIZES } from '@mews-distributor/common';
import { getStringArrayProp, getStringOrUndefinedProp, getStringProp } from '@mews/fp';
import { createPropsExtender, RouterService } from '@mews/framework';
import { TextKey } from '@mews/text-keys';
import { ApplicationState } from '../../types';
import { WithCategoryId } from '../../types/selectors/selectorPropTypes';
import { mapWithIndexes } from '../../utils/func';
import { getImageBaseUrl } from '../configuration/selectors';
import { getSortedCategoryImageAssignmentsImageIds } from '../entities/categoryImageAssignments/selectors';
import { getEntities } from '../entities/getEntities';
import { localizeText } from '../globalization/selectors';
import { getRoomId, getSelectedHotelId } from '../resourceOrder/selectors';
import { getAvailabilityCategoryIds } from '../serviceAvailability/selectors';
import { Categories } from './types';

const getCategoriesSlice = flow(getEntities, (entities) => entities.categories);

export const getCategories = flow(getCategoriesSlice, (categoriesSlice) => categoriesSlice.data);
export const getCategoriesMeta = flow(getCategoriesSlice, (categoriesSlice) => categoriesSlice.meta);

export const getSelectedEnterpriseCategories = createSelector(
    getCategories,
    getSelectedHotelId,
    getAvailabilityCategoryIds,
    (categories, enterpriseId, categoryIds) =>
        pickBy((category) => category.enterpriseId === enterpriseId && categoryIds.includes(category.id), categories)
);

export const getCategory = createSelector(getStringProp('categoryId'), getCategories, get<Categories, string>);
export const isCategoryNil = flow(getCategory, isNil);

export const getCategoryName = flow(getCategory, get('name'));

export const getCategoryEnterpriseId = flow(getCategory, get('enterpriseId'));
export const categoryEnterpriseIdPropsExtender = createPropsExtender('enterpriseId', getCategoryEnterpriseId);

const getCategoryNormalBedCount = flow(getCategory, get('normalBedCount'));
const getCategoryExtraBedCount = flow(getCategory, get('extraBedCount'));

export const getCategoryMaxPersonCount = createSelector(
    getCategoryNormalBedCount,
    getCategoryExtraBedCount,
    (normalBedCount, extraBedCount) => (normalBedCount as number) + (extraBedCount as number)
);

export const getCategorySpaceTypeEnumName = flow(getCategory, get('spaceType'));

export const getSelectedCategory = createSelector(
    getCategories,
    getRoomId,
    // @ts-expect-error $TSFixMeError: Type 'undefined' cannot be used as an index type.
    (categories, categoryId) => categories[categoryId]
);

export const getCategoryGalleryImage = createSelector(
    getImageBaseUrl,
    getStringProp('imageId'),
    getStringOrUndefinedProp('alt'),
    getStringOrUndefinedProp('altOpener'),
    (imageBaseUrl, imageId, alt, altOpener) => ({
        id: imageId,
        // @ts-expect-error $TSFixMeError: Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
        src: RouterService.imageUrl(imageId, imageBaseUrl, { quality: IMAGE_DEFAULT_QUALITY }),
        sizes: IMAGE_DEFAULT_SIZES,
        alt,
        altOpener,
    })
);

export const getCategoryGalleryImages = createSelector(
    identity,
    getStringArrayProp('imageIds'),
    (state: ApplicationState, imageIds: string[]) => {
        const spaceImageText = localizeText(TextKey.SpaceImage)(state);
        const viewAllPhotosText = localizeText(TextKey.ViewAllPhotos)(state);
        const ofText = localizeText(TextKey.Of)(state);

        return mapWithIndexes((imageId: string, imageIndex: number) => {
            const alt = `${spaceImageText} ${imageIndex + 1} ${ofText} ${imageIds.length}`;
            const altOpener = `${spaceImageText} ${imageIndex + 1} ${ofText} ${imageIds.length}, ${viewAllPhotosText}`;
            return getCategoryGalleryImage(state, { imageId, alt, altOpener });
        }, imageIds);
    }
);

export const getSortedCategoryGalleryImages = (state: ApplicationState, { categoryId }: WithCategoryId) => {
    const imageIds = getSortedCategoryImageAssignmentsImageIds(state, { categoryId });
    return getCategoryGalleryImages(state, { imageIds });
};
